'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  category: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'governance',
    title: 'BC Governance & Policy',
    description: 'Business continuity program governance and policy framework',
    questions: [
      { id: 'gv1', text: 'Do you have a documented business continuity policy approved by senior management?', category: 'Policy', weight: 10 },
      { id: 'gv2', text: 'Is there executive sponsorship for the BC program?', category: 'Sponsorship', weight: 9 },
      { id: 'gv3', text: 'Are roles and responsibilities for BC clearly defined?', category: 'Roles', weight: 9 },
      { id: 'gv4', text: 'Is there a dedicated BC manager or team?', category: 'Resources', weight: 8 },
      { id: 'gv5', text: 'Is the BC policy reviewed and updated annually?', category: 'Maintenance', weight: 8 },
      { id: 'gv6', text: 'Does the BC program align with industry standards (ISO 22301)?', category: 'Standards', weight: 7 },
    ],
  },
  {
    id: 'bia',
    title: 'Business Impact Analysis',
    description: 'Understanding critical business processes and their requirements',
    questions: [
      { id: 'bi1', text: 'Have you conducted a Business Impact Analysis (BIA)?', category: 'BIA', weight: 10 },
      { id: 'bi2', text: 'Are critical business functions identified and prioritized?', category: 'Prioritization', weight: 10 },
      { id: 'bi3', text: 'Have Recovery Time Objectives (RTO) been defined for critical functions?', category: 'RTO', weight: 10 },
      { id: 'bi4', text: 'Have Recovery Point Objectives (RPO) been defined for critical data?', category: 'RPO', weight: 10 },
      { id: 'bi5', text: 'Are dependencies between business functions mapped?', category: 'Dependencies', weight: 9 },
      { id: 'bi6', text: 'Is the financial impact of downtime quantified?', category: 'Impact', weight: 8 },
      { id: 'bi7', text: 'Is the BIA updated annually or when significant changes occur?', category: 'Maintenance', weight: 8 },
    ],
  },
  {
    id: 'risk-assessment',
    title: 'Risk Assessment',
    description: 'Identifying and assessing threats to business continuity',
    questions: [
      { id: 'ra1', text: 'Have potential disruption scenarios been identified?', category: 'Scenarios', weight: 10 },
      { id: 'ra2', text: 'Are risks assessed for likelihood and impact?', category: 'Assessment', weight: 9 },
      { id: 'ra3', text: 'Have single points of failure been identified?', category: 'Vulnerabilities', weight: 9 },
      { id: 'ra4', text: 'Are geographic risks considered (natural disasters, political)?', category: 'Location', weight: 8 },
      { id: 'ra5', text: 'Are supply chain and vendor risks assessed?', category: 'Third Party', weight: 9 },
      { id: 'ra6', text: 'Are pandemic and public health scenarios considered?', category: 'Health', weight: 8 },
    ],
  },
  {
    id: 'bc-plans',
    title: 'Business Continuity Plans',
    description: 'Documented plans for maintaining operations during disruption',
    questions: [
      { id: 'bp1', text: 'Do documented BC plans exist for critical business functions?', category: 'Documentation', weight: 10 },
      { id: 'bp2', text: 'Do plans include clear activation criteria and procedures?', category: 'Activation', weight: 9 },
      { id: 'bp3', text: 'Are alternative operating procedures documented?', category: 'Procedures', weight: 9 },
      { id: 'bp4', text: 'Do plans include resource requirements (people, technology, facilities)?', category: 'Resources', weight: 9 },
      { id: 'bp5', text: 'Are plans accessible during a disruption (offline copies, cloud access)?', category: 'Accessibility', weight: 9 },
      { id: 'bp6', text: 'Do plans include return to normal procedures?', category: 'Recovery', weight: 8 },
    ],
  },
  {
    id: 'dr',
    title: 'Disaster Recovery',
    description: 'IT systems and data recovery capabilities',
    questions: [
      { id: 'dr1', text: 'Is there a documented IT Disaster Recovery Plan?', category: 'DR Plan', weight: 10 },
      { id: 'dr2', text: 'Are critical systems backed up regularly?', category: 'Backup', weight: 10 },
      { id: 'dr3', text: 'Are backups stored off-site or in a separate geographic location?', category: 'Backup Location', weight: 10 },
      { id: 'dr4', text: 'Is there redundant infrastructure for critical systems?', category: 'Redundancy', weight: 9 },
      { id: 'dr5', text: 'Can systems be recovered within defined RTOs?', category: 'Recovery', weight: 10 },
      { id: 'dr6', text: 'Is there a secondary data center or cloud failover capability?', category: 'Failover', weight: 8 },
      { id: 'dr7', text: 'Are backup restoration procedures tested regularly?', category: 'Testing', weight: 9 },
    ],
  },
  {
    id: 'crisis-management',
    title: 'Crisis Management',
    description: 'Organizational response to major incidents and crises',
    questions: [
      { id: 'cm1', text: 'Is there a Crisis Management Team (CMT) with defined roles?', category: 'Team', weight: 10 },
      { id: 'cm2', text: 'Are crisis escalation procedures documented?', category: 'Escalation', weight: 9 },
      { id: 'cm3', text: 'Is there a crisis communication plan (internal and external)?', category: 'Communication', weight: 9 },
      { id: 'cm4', text: 'Are emergency contact lists maintained and current?', category: 'Contacts', weight: 9 },
      { id: 'cm5', text: 'Is there a dedicated crisis command center or virtual equivalent?', category: 'Command', weight: 7 },
      { id: 'cm6', text: 'Are there procedures for media and stakeholder communication?', category: 'Media', weight: 8 },
    ],
  },
  {
    id: 'testing',
    title: 'Testing & Exercising',
    description: 'Regular testing and validation of BC/DR capabilities',
    questions: [
      { id: 'te1', text: 'Are BC plans tested at least annually?', category: 'Frequency', weight: 10 },
      { id: 'te2', text: 'Do tests include tabletop exercises with key stakeholders?', category: 'Tabletop', weight: 9 },
      { id: 'te3', text: 'Do tests include technical failover tests?', category: 'Technical', weight: 9 },
      { id: 'te4', text: 'Are test results documented and reviewed?', category: 'Documentation', weight: 9 },
      { id: 'te5', text: 'Are lessons learned incorporated into plan improvements?', category: 'Improvement', weight: 9 },
      { id: 'te6', text: 'Do tests involve realistic scenarios?', category: 'Realism', weight: 8 },
    ],
  },
  {
    id: 'awareness',
    title: 'Training & Awareness',
    description: 'BC training and organizational awareness',
    questions: [
      { id: 'aw1', text: 'Are employees aware of their BC roles and responsibilities?', category: 'Awareness', weight: 9 },
      { id: 'aw2', text: 'Is BC training provided to key personnel?', category: 'Training', weight: 9 },
      { id: 'aw3', text: 'Do new employees receive BC orientation?', category: 'Onboarding', weight: 7 },
      { id: 'aw4', text: 'Are BC contact lists and procedures easily accessible to staff?', category: 'Access', weight: 8 },
      { id: 'aw5', text: 'Is there regular communication about BC program updates?', category: 'Communication', weight: 7 },
    ],
  },
];

export default function BusinessContinuity() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'business-continuity',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { category: string; question: string; status: string; priority: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({
            category: q.category,
            question: q.text,
            status: 'Partial',
            priority: q.weight >= 10 ? 'Critical' : q.weight >= 8 ? 'High' : 'Medium',
          });
        } else if (answer === 'no') {
          gaps.push({
            category: q.category,
            question: q.text,
            status: 'Gap',
            priority: q.weight >= 10 ? 'Critical' : q.weight >= 8 ? 'High' : 'Medium',
          });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => {
        const priorityOrder = { Critical: 0, High: 1, Medium: 2 };
        return priorityOrder[a.priority as keyof typeof priorityOrder] - priorityOrder[b.priority as keyof typeof priorityOrder];
      }),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getReadinessLevel = (score: number) => {
    if (score >= 90) return { level: 'Mature', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 75) return { level: 'Established', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (score >= 60) return { level: 'Developing', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 40) return { level: 'Basic', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Initial', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const readiness = getReadinessLevel(score);

    let md = `# Business Continuity Readiness Assessment\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `## Overall Readiness Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Maturity Level:** ${readiness.level}\n\n`;

    md += `## Domain Scores\n\n`;
    md += `| Domain | Score |\n`;
    md += `|--------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Improvement Areas\n\n`;

      md += `### Critical Priority\n\n`;
      const critical = gaps.filter((g) => g.priority === 'Critical');
      if (critical.length > 0) {
        critical.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No critical gaps identified.\n`;
      }

      md += `\n### High Priority\n\n`;
      const high = gaps.filter((g) => g.priority === 'High');
      if (high.length > 0) {
        high.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No high priority gaps identified.\n`;
      }

      md += `\n### Medium Priority\n\n`;
      const medium = gaps.filter((g) => g.priority === 'Medium');
      if (medium.length > 0) {
        medium.forEach((gap) => {
          md += `- **${gap.category}:** ${gap.question} (${gap.status})\n`;
        });
      } else {
        md += `No medium priority gaps identified.\n`;
      }
    }

    md += `\n## Key BC Metrics to Track\n\n`;
    md += `- **RTO (Recovery Time Objective):** Maximum acceptable downtime\n`;
    md += `- **RPO (Recovery Point Objective):** Maximum acceptable data loss\n`;
    md += `- **MTPD (Maximum Tolerable Period of Disruption):** Survival threshold\n`;
    md += `- **MBCO (Minimum Business Continuity Objective):** Minimum service level\n`;

    md += `\n## Standards Reference\n\n`;
    md += `- **ISO 22301:** Business continuity management systems\n`;
    md += `- **ISO 22317:** Business impact analysis guidelines\n`;
    md += `- **ISO 22313:** Business continuity management guidance\n`;
    md += `- **NIST SP 800-34:** Contingency Planning Guide\n`;

    md += `\n## Recommended Actions\n\n`;
    if (score < 75) {
      md += `1. Conduct or update Business Impact Analysis\n`;
      md += `2. Document BC plans for critical functions\n`;
      md += `3. Establish Crisis Management Team\n`;
      md += `4. Implement regular backup testing\n`;
      md += `5. Schedule annual BC plan exercises\n`;
      md += `6. Train key personnel on BC responsibilities\n`;
    } else {
      md += `1. Maintain annual testing cycle\n`;
      md += `2. Continue program improvement\n`;
      md += `3. Consider ISO 22301 certification\n`;
      md += `4. Benchmark against industry peers\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO Business Continuity Readiness*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bc-readiness-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const { score, gaps } = calculateScore();
    const readiness = getReadinessLevel(score);

    const gapItems = gaps.map((gap) => ({
      id: gap.category.toLowerCase().replace(/\s+/g, '-'),
      title: gap.question,
      severity: gap.priority === 'Critical' ? 'critical' as const :
                gap.priority === 'High' ? 'high' as const : 'medium' as const,
      description: `${gap.status} - ${gap.category}`,
    }));

    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const recommendations = sections
      .filter((s) => getSectionScore(s.id) < 80)
      .sort((a, b) => getSectionScore(a.id) - getSectionScore(b.id))
      .map((section) => ({
        id: section.id,
        title: `Improve ${section.title}`,
        priority: getSectionScore(section.id) < 40 ? 'high' as const :
                  getSectionScore(section.id) < 60 ? 'high' as const : 'medium' as const,
        description: `Current score: ${getSectionScore(section.id)}%. Focus on implementing BC controls for ${section.title.toLowerCase()}.`,
      }));

    const assessment = await saveResult({
      name: `Business Continuity Readiness - ${new Date().toLocaleDateString()}`,
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gapItems,
      recommendations,
      metadata: {
        readinessLevel: readiness.level,
        totalQuestions: sections.reduce((sum, s) => sum + s.questions.length, 0),
        answeredQuestions: Object.keys(answers).length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              Operational Resilience
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              Business Continuity Readiness
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your organization&apos;s ability to maintain operations during disruptions.
              Evaluate BIA, DR capabilities, crisis management, and testing practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Business Continuity</h3>
              <p className="text-foreground-secondary text-sm">
                The capability to continue delivery of products or services at acceptable
                predefined levels following a disruptive incident. BC is about planning
                for the unexpected.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Why It Matters</h3>
              <p className="text-foreground-secondary text-sm">
                Organizations face threats from natural disasters, cyber attacks,
                pandemics, and system failures. Strong BC programs reduce downtime,
                protect revenue, and maintain customer trust.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Assessment Areas</h3>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-foreground-secondary">
              {sections.map((section) => (
                <div key={section.id} className="flex gap-2">
                  <span className="text-accent">•</span>
                  <span>{section.title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Key BC Metrics</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-foreground-secondary">
              <div>
                <p className="text-foreground font-medium">RTO - Recovery Time Objective</p>
                <p>Maximum acceptable time to restore function</p>
              </div>
              <div>
                <p className="text-foreground font-medium">RPO - Recovery Point Objective</p>
                <p>Maximum acceptable data loss measured in time</p>
              </div>
              <div>
                <p className="text-foreground font-medium">MTPD - Max Tolerable Period</p>
                <p>Time before disruption threatens survival</p>
              </div>
              <div>
                <p className="text-foreground font-medium">MBCO - Min Business Continuity</p>
                <p>Minimum service level during disruption</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start BC Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.category}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const readiness = getReadinessLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            Business Continuity Readiness Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${readiness.bg}`}>
            <span className={`text-4xl font-bold ${readiness.color}`}>{score}%</span>
            <p className={`${readiness.color} font-medium mt-1`}>{readiness.level} Maturity</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Domain Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionReadiness = getReadinessLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionReadiness.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 75
                          ? 'bg-blue-500'
                          : sectionScore >= 60
                          ? 'bg-yellow-500'
                          : sectionScore >= 40
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Improvement Areas ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.category}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.priority === 'Critical'
                          ? 'bg-red-500/20 text-red-400'
                          : gap.priority === 'High'
                          ? 'bg-accent-muted text-accent'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {gap.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Standards & Frameworks</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-accent">ISO 22301:</span> BC management systems requirements</p>
            <p><span className="text-accent">ISO 22317:</span> Business impact analysis guidance</p>
            <p><span className="text-accent">NIST SP 800-34:</span> IT contingency planning guide</p>
            <p><span className="text-accent">BCI Good Practice:</span> Global BC best practices</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          {commandCenterEnabled && !saved && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save to Command Center'}
            </button>
          )}
          {saved && (
            <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Saved to Command Center
            </div>
          )}
          {saveError && (
            <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
              {saveError}
            </div>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
